import type { Material } from './types.js';

/** SPECIFICATION.md clause 15.3 — the ten diaper colours. */
export const PALETTE: readonly (readonly [string, string])[] = [
  ['Brick red', '#B5523B'],
  ['Deep red', '#7E3027'],
  ['Buff', '#D4A35B'],
  ['Cream', '#E7D7AD'],
  ['Brown', '#70483A'],
  ['Charcoal', '#3D4248'],
  ['Blue', '#315A7D'],
  ['Green', '#486B4A'],
  ['White', '#F3F0E8'],
  ['Black', '#202225'],
];

export const DEFAULT_BRICK_COLOUR = '#B5523B';

const HEX = /^#[0-9a-fA-F]{6}$/;

export function isValidColour(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value);
}

export function normaliseColour(value: unknown): string {
  if (!isValidColour(value)) throw new Error(`Invalid brick colour: ${String(value)}`);
  return value.toUpperCase();
}

/** The palette name for a colour, or a generated one. */
export function nameForColour(colour: string): string {
  const upper = colour.toUpperCase();
  return PALETTE.find(([, hex]) => hex.toUpperCase() === upper)?.[0] ?? `Colour ${upper}`;
}

export function makeMaterial(id: string, colour: string, name?: string): Material {
  const hex = normaliseColour(colour);
  return { id, name: name ?? nameForColour(hex), colour: hex, texture: null, roughness: null };
}

/** The material every new project starts with. */
export function defaultMaterials(): Material[] {
  return [makeMaterial('m1', DEFAULT_BRICK_COLOUR)];
}

/** Build the full ten-colour set, for a project that wants the whole palette. */
export function paletteMaterials(): Material[] {
  return PALETTE.map(([name, colour], index) => makeMaterial(`m${index + 1}`, colour, name));
}

export function nextMaterialId(existing: readonly Material[]): string {
  let n = existing.length + 1;
  const taken = new Set(existing.map((m) => m.id));
  while (taken.has(`m${n}`)) n += 1;
  return `m${n}`;
}
